import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { AuthLayout } from '@/components/layout/auth-layout';
import {
  apiClient,
  type ApiError,
  type InvitePreviewResponse,
} from '@/lib/api-client';
import { useAuthStore } from '@/lib/auth-store';

const ROLE_LABELS: Record<InvitePreviewResponse['legacyRole'], string> = {
  owner: 'Owner',
  admin: 'Admin',
  lawyer: 'Advogado(a)',
  assistant: 'Assistente',
  financial: 'Financeiro',
  // Mantido só para tipar convites legados. O aceite desses convites é
  // recusado pelo backend. Ver docs/PORTAL-CLIENTE.md.
  client_read_only: 'Acesso revogado (legado)',
};

function extractApiMessage(err: unknown, fallback: string): string {
  const body = (err as { body?: unknown })?.body;
  const raw = (body as { message?: unknown })?.message;
  if (typeof raw === 'string') return raw;
  return (err as Error)?.message ?? fallback;
}

export default function AceitarConvitePage() {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const status = useAuthStore((s) => s.status);
  const user = useAuthStore((s) => s.user);
  const hydrateFromTokens = useAuthStore((s) => s.hydrateFromTokens);
  const signOut = useAuthStore((s) => s.signOut);
  const bootstrap = useAuthStore((s) => s.bootstrap);

  const [preview, setPreview] = useState<InvitePreviewResponse | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(true);

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await apiClient.invitePreview(token);
        if (!cancelled) setPreview(res);
      } catch (err) {
        if (!cancelled) {
          const s = (err as ApiError | undefined)?.status;
          if (s === 404) setPreviewError('Convite não encontrado.');
          else if (s === 410) {
            setPreviewError(
              extractApiMessage(err, 'Convite expirou ou já foi aceito.'),
            );
          } else {
            setPreviewError(
              extractApiMessage(err, 'Falha ao carregar convite.'),
            );
          }
        }
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function handleSignUpAndAccept(e: React.FormEvent) {
    e.preventDefault();
    setActionError(null);
    if (password !== confirmPassword) {
      setActionError('As senhas não conferem.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await apiClient.inviteSignUpAccept(token, { name, password });
      hydrateFromTokens({
        user: {
          id: res.user.id,
          email: res.user.email,
          name: res.user.name,
          mfaEnabled: false,
        },
        accessToken: res.accessToken,
        tenantId: res.tenant.id,
      });
      navigate('/escritorio', { replace: true });
    } catch (err) {
      const s = (err as ApiError | undefined)?.status;
      if (s === 409) {
        setActionError(
          extractApiMessage(
            err,
            'Já existe conta com este email. Use o link "Já tenho conta" abaixo.',
          ),
        );
      } else if (s === 410) {
        setActionError(
          extractApiMessage(err, 'Convite expirou ou já foi utilizado.'),
        );
      } else if (s === 404) {
        setActionError('Convite não encontrado.');
      } else {
        setActionError(extractApiMessage(err, 'Falha ao criar conta.'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleAcceptAsLoggedUser() {
    setActionError(null);
    setSubmitting(true);
    try {
      await apiClient.inviteAccept(token);
      // Re-bootstrap pra atualizar memberships e tenantId.
      await bootstrap();
      navigate('/escritorio', { replace: true });
    } catch (err) {
      setActionError(extractApiMessage(err, 'Falha ao aceitar convite.'));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSignOutAndRetry() {
    await signOut();
    // Recarrega a propria pagina pra reavaliar o caso A.
    window.location.reload();
  }

  if (previewLoading) {
    return (
      <AuthLayout>
        <Card>
          <CardHeader>
            <CardTitle>Carregando convite...</CardTitle>
          </CardHeader>
        </Card>
      </AuthLayout>
    );
  }

  if (previewError || !preview) {
    return (
      <AuthLayout>
        <Card>
          <CardHeader>
            <CardTitle>Convite indisponível</CardTitle>
            <CardDescription>
              {previewError ?? 'Erro desconhecido.'}
            </CardDescription>
          </CardHeader>
          <CardFooter>
            <Link to="/sign-in" className="text-sm underline">
              Voltar
            </Link>
          </CardFooter>
        </Card>
      </AuthLayout>
    );
  }

  const roleLabel = ROLE_LABELS[preview.legacyRole] ?? preview.legacyRole;

  // Caso B: usuario logado
  if (status === 'authenticated' && user?.email) {
    const sameEmail =
      user.email.toLowerCase() === preview.inviteeEmail.toLowerCase();

    if (!sameEmail) {
      return (
        <AuthLayout>
          <Card>
            <CardHeader>
              <CardTitle>Email do convite não bate</CardTitle>
              <CardDescription>
                Você está logado como <strong>{user.email}</strong>, mas o convite
                foi enviado para <strong>{preview.inviteeEmail}</strong>. Saia e
                entre com o email correto, ou peça um novo convite.
              </CardDescription>
            </CardHeader>
            <CardFooter>
              <Button onClick={handleSignOutAndRetry} className="w-full">
                Sair e tentar de novo
              </Button>
            </CardFooter>
          </Card>
        </AuthLayout>
      );
    }

    return (
      <AuthLayout>
        <Card>
          <CardHeader>
            <CardTitle>Aceitar convite para {preview.tenantName}</CardTitle>
            <CardDescription>
              Você está logado como <strong>{user.email}</strong>. Será adicionado
              ao escritório como <strong>{roleLabel}</strong>.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {actionError && (
              <p className="text-sm text-red-600" role="alert">
                {actionError}
              </p>
            )}
          </CardContent>
          <CardFooter>
            <Button
              onClick={handleAcceptAsLoggedUser}
              disabled={submitting}
              className="w-full"
            >
              {submitting ? 'Aceitando...' : 'Aceitar convite'}
            </Button>
          </CardFooter>
        </Card>
      </AuthLayout>
    );
  }

  // Caso A: usuario nao logado -> form signup-and-accept
  return (
    <AuthLayout>
      <Card>
        <CardHeader>
          <CardTitle>Você foi convidado para {preview.tenantName}</CardTitle>
          <CardDescription>
            Crie sua conta para entrar no escritório como{' '}
            <strong>{roleLabel}</strong>.
            {preview.invitedByName
              ? ` Convite enviado por ${preview.invitedByName}.`
              : ''}
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSignUpAndAccept}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">E-mail</Label>
              <Input
                id="email"
                type="email"
                value={preview.inviteeEmail}
                disabled
                readOnly
                autoComplete="email"
              />
              <p className="text-xs text-[var(--fg-muted)]">
                Esse é o email do convite e não pode ser alterado.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">Nome completo</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                minLength={2}
                maxLength={120}
                autoComplete="name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Senha</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={12}
                autoComplete="new-password"
              />
              <p className="text-xs text-[var(--fg-muted)]">
                Pelo menos 12 caracteres, com maiúscula, minúscula e dígito.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirmar senha</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={12}
                autoComplete="new-password"
              />
            </div>
            {actionError && (
              <p className="text-sm text-red-600" role="alert">
                {actionError}
              </p>
            )}
          </CardContent>
          <CardFooter className="flex-col gap-3">
            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting
                ? 'Criando conta...'
                : 'Criar conta e entrar no escritório'}
            </Button>
            <p className="text-center text-sm text-[var(--fg-muted)]">
              Já tem conta com {preview.inviteeEmail}?{' '}
              <Link
                to={`/sign-in?redirect=${encodeURIComponent(`/aceitar-convite/${token}`)}`}
                className="underline"
              >
                Entrar
              </Link>
            </p>
          </CardFooter>
        </form>
      </Card>
    </AuthLayout>
  );
}
